"use client";

import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";

export interface UploadedFile {
  url: string;
  pathname: string;
  contentType: string;
  contentDisposition: string;
  downloadUrl: string;
}

interface UseUploadFileProps {
  endpoint: "avatar" | "listing";
  input?: Record<string, unknown>;
  onUploadBegin?: () => void;
  onUploadProgress?: (progress: number) => void;
  onUploadComplete?: (file: UploadedFile) => void;
  onUploadError?: (error: unknown) => void;
}

export function useUploadFile({
  endpoint,
  input,
  onUploadBegin,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
}: UseUploadFileProps) {
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFile>();
  const [uploadingFile, setUploadingFile] = React.useState<File>();
  const [progress, setProgress] = React.useState<number>(0);
  const [isUploading, setIsUploading] = React.useState(false);

  async function uploadFile(file: File) {
    setIsUploading(true);
    setUploadingFile(file);
    setProgress(0);

    try {
      // Notify upload begin
      onUploadBegin?.();

      // Build upload URL with query parameters for input data
      const params = new URLSearchParams();
      if (input) {
        Object.entries(input).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }
      const uploadUrl = `/api/blob/${endpoint}${params.toString() ? `?${params.toString()}` : ""}`;

      // Create FormData for upload
      const formData = new FormData();
      formData.append("file", file);

      // Upload using native fetch with FormData
      // IMPORTANT: Do NOT set Content-Type header - browser sets it automatically with boundary
      const response = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Upload failed with status ${response.status}`,
        );
      }

      const data = await response.json();

      // Map response to UploadedFile format
      const uploadedFileData: UploadedFile = {
        url: data.url,
        pathname: data.key,
        contentType: data.type,
        contentDisposition: "inline",
        downloadUrl: data.url,
      };

      // Simulate progress completion (since native fetch doesn't support progress events easily)
      setProgress(100);
      onUploadProgress?.(100);

      setUploadedFile(uploadedFileData);
      onUploadComplete?.(uploadedFileData);

      return uploadedFileData;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      const message =
        errorMessage.length > 0
          ? errorMessage
          : "Something went wrong, please try again later.";

      toast(message);
      onUploadError?.(error);

      throw error;
    } finally {
      setProgress(0);
      setIsUploading(false);
      setUploadingFile(undefined);
    }
  }

  return {
    isUploading,
    progress,
    uploadedFile,
    uploadFile,
    uploadingFile,
  };
}

export function getErrorMessage(err: unknown) {
  const unknownError = "Something went wrong, please try again later.";

  if (err instanceof z.ZodError) {
    const errors = err.issues.map((issue) => {
      return issue.message;
    });

    return errors.join("\n");
  } else if (err instanceof Error) {
    return err.message;
  } else {
    return unknownError;
  }
}

export function showErrorToast(err: unknown) {
  const errorMessage = getErrorMessage(err);

  return toast(errorMessage);
}
